// Nutritionix API Wrapper
// Provides functions to search for foods, scan barcodes, and get nutrition data

const NUTRITIONIX_PROXY_URL = '/.netlify/functions/nutritionix-api';

/**
 * Search for foods using autocomplete (instant search)
 * @param {string} query - Search query (e.g., "chicken", "apple")
 * @returns {Promise<{common: Array, branded: Array}>} - Search results
 */
export async function searchFoods(query) {
  try {
    const response = await fetch(NUTRITIONIX_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        endpoint: '/v2/search/instant',
        method: 'GET',
        params: { query }
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to search foods');
    }

    const data = await response.json();
    return {
      common: data.common || [],
      branded: data.branded || []
    };
  } catch (error) {
    console.error('Search foods error:', error);
    throw error;
  }
}

/**
 * Get nutrition data from natural language query
 * @param {string} query - Natural language query (e.g., "2 eggs and bacon", "chicken breast")
 * @returns {Promise<Array>} - Array of food items with full nutrition data
 */
export async function getNutritionFromNaturalLanguage(query) {
  try {
    const response = await fetch(NUTRITIONIX_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        endpoint: '/v2/natural/nutrients',
        method: 'POST',
        body: { query }
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get nutrition data');
    }

    const data = await response.json();
    return data.foods || [];
  } catch (error) {
    console.error('Natural language nutrition error:', error);
    throw error;
  }
}

/**
 * Look up nutrition data by UPC barcode
 * @param {string} upc - UPC barcode number
 * @returns {Promise<Object>} - Food item with full nutrition data
 */
export async function getNutritionByUPC(upc) {
  console.log('🔍 getNutritionByUPC called with:', upc);

  try {
    const requestBody = {
      endpoint: '/v2/search/item',
      method: 'GET',
      params: { upc }
    };

    console.log('📤 Sending request to proxy:', NUTRITIONIX_PROXY_URL);
    console.log('Request body:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(NUTRITIONIX_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    console.log('📥 Response status:', response.status, response.statusText);

    if (!response.ok) {
      let errorMessage = 'Product not found';
      try {
        const error = await response.json();
        console.error('❌ Error response:', error);
        errorMessage = error.error || error.message || errorMessage;
      } catch (e) {
        const errorText = await response.text();
        console.error('❌ Raw error response:', errorText);
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('📦 API response data:', data);

    if (!data.foods || data.foods.length === 0) {
      console.error('❌ No foods found in response');
      throw new Error('Product not found in database');
    }

    console.log('✅ Found food:', data.foods[0].food_name);
    return data.foods[0];
  } catch (error) {
    console.error('❌ UPC lookup error:', error);
    console.error('Error type:', error.constructor.name);
    console.error('Error message:', error.message);
    throw error;
  }
}

/**
 * Look up nutrition data by Nutritionix item ID
 * @param {string} nixItemId - Nutritionix item ID from search results
 * @returns {Promise<Object>} - Food item with full nutrition data
 */
export async function getNutritionByItemId(nixItemId) {
  try {
    const response = await fetch(NUTRITIONIX_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        endpoint: '/v2/search/item',
        method: 'GET',
        params: { nix_item_id: nixItemId }
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Item not found');
    }

    const data = await response.json();
    if (!data.foods || data.foods.length === 0) {
      throw new Error('Item not found in database');
    }

    return data.foods[0];
  } catch (error) {
    console.error('Item lookup error:', error);
    throw error;
  }
}

/**
 * Calculate nutrition for a different serving size
 * Uses the formula: (new_serving_weight / original_serving_weight) * nutrients
 * @param {Object} food - Food object from API
 * @param {number} newServingWeight - New serving weight in grams
 * @returns {Object} - Food object with recalculated nutrition
 */
export function calculateServingSize(food, newServingWeight) {
  const originalWeight = food.serving_weight_grams;
  const multiplier = newServingWeight / originalWeight;

  return {
    ...food,
    serving_weight_grams: newServingWeight,
    nf_calories: Math.round(food.nf_calories * multiplier * 10) / 10,
    nf_protein: Math.round(food.nf_protein * multiplier * 10) / 10,
    nf_total_fat: Math.round((food.nf_total_fat || 0) * multiplier * 10) / 10,
    nf_total_carbohydrate: Math.round((food.nf_total_carbohydrate || 0) * multiplier * 10) / 10,
    nf_sugars: Math.round((food.nf_sugars || 0) * multiplier * 10) / 10,
    nf_dietary_fiber: Math.round((food.nf_dietary_fiber || 0) * multiplier * 10) / 10
  };
}

/**
 * Calculate nutrition for a different quantity (e.g., 2 servings instead of 1)
 * @param {Object} food - Food object from API
 * @param {number} newQuantity - New quantity
 * @returns {Object} - Food object with recalculated nutrition
 */
export function calculateQuantity(food, newQuantity) {
  const originalQuantity = food.serving_qty;
  const multiplier = newQuantity / originalQuantity;

  return {
    ...food,
    serving_qty: newQuantity,
    nf_calories: Math.round(food.nf_calories * multiplier * 10) / 10,
    nf_protein: Math.round(food.nf_protein * multiplier * 10) / 10,
    nf_total_fat: Math.round((food.nf_total_fat || 0) * multiplier * 10) / 10,
    nf_total_carbohydrate: Math.round((food.nf_total_carbohydrate || 0) * multiplier * 10) / 10,
    nf_sugars: Math.round((food.nf_sugars || 0) * multiplier * 10) / 10,
    nf_dietary_fiber: Math.round((food.nf_dietary_fiber || 0) * multiplier * 10) / 10
  };
}

/**
 * Validate UPC barcode using check digit algorithm
 * @param {string} upc - UPC barcode
 * @returns {boolean} - True if valid
 */
export function validateUPC(upc) {
  if (!upc || upc.length < 8) return false;

  // Remove any non-digit characters
  const digits = upc.replace(/\D/g, '');

  // Accept UPC-A (12 digits), EAN-13 (13 digits), UPC-E (8 digits), EAN-8 (8 digits)
  if (![8, 12, 13].includes(digits.length)) return false;

  // Calculate check digit for UPC-A/EAN-13 (12 or 13 digits)
  if (digits.length === 12 || digits.length === 13) {
    let sum = 0;
    const len = digits.length;
    for (let i = 0; i < len - 1; i++) {
      const digit = parseInt(digits[i]);
      // For EAN-13: multiply odd positions (from right) by 3
      // For UPC-A: multiply odd positions (from left) by 3
      if (len === 13) {
        // EAN-13: count from right (reverse logic)
        sum += ((len - 1 - i) % 2 === 0) ? digit * 3 : digit;
      } else {
        // UPC-A: count from left
        sum += (i % 2 === 0) ? digit * 3 : digit;
      }
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    const providedCheckDigit = parseInt(digits[len - 1]);
    return checkDigit === providedCheckDigit;
  }

  // For UPC-E and EAN-8, just check it's 8 digits (simplified validation)
  return true;
}

/**
 * Format food data for display
 * @param {Object} food - Food object from API
 * @returns {Object} - Formatted food data
 */
export function formatFoodData(food) {
  return {
    name: food.food_name,
    brandName: food.brand_name || food.nix_brand_name,
    servingQty: food.serving_qty,
    servingUnit: food.serving_unit,
    servingWeight: food.serving_weight_grams,
    calories: Math.round(food.nf_calories),
    protein: Math.round(food.nf_protein * 10) / 10,
    fat: Math.round((food.nf_total_fat || 0) * 10) / 10,
    carbs: Math.round((food.nf_total_carbohydrate || 0) * 10) / 10,
    sugar: Math.round((food.nf_sugars || 0) * 10) / 10,
    fiber: Math.round((food.nf_dietary_fiber || 0) * 10) / 10,
    photo: food.photo?.thumb || food.photo?.highres,
    altMeasures: food.alt_measures || []
  };
}
